import React, { useId, useState } from 'react';
import { Button, Input } from 'antd';
import {
  KofiRequiredError,
  RedeemError,
  ToolGateStatus,
  redeemKofiCode,
} from './kofi.service';

type KofiSupportPanelProps = {
  /** The 402 the tool submit received, carrying the Ko-fi page and minimum. */
  required: KofiRequiredError;
  /** Called with the updated allowance after a code is accepted. */
  onRedeemed: (status: ToolGateStatus) => void;
};

/** Asks the visitor to support on Ko-fi and redeem the emailed code. */
const KofiSupportPanel: React.FC<KofiSupportPanelProps> = ({
  required,
  onRedeemed,
}) => {
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseId = useId();
  const inputId = `${baseId}-code`;
  const errorId = `${baseId}-error`;

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!code.trim()) {
      setError('Enter the code from your Ko-fi email.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      onRedeemed(await redeemKofiCode(code));
    } catch (err: unknown) {
      setError(
        err instanceof RedeemError
          ? err.message
          : "We couldn't check that code. Try again in a minute."
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      className="public-result-panel mt-4"
      aria-labelledby={`${baseId}-title`}
    >
      <h3
        id={`${baseId}-title`}
        className="jo-heading mb-2 text-base font-black"
      >
        Unlock 5 more checks
      </h3>
      <p className="jo-body-subtle mb-3 text-sm">{required.message}</p>
      <a
        href={required.kofiUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="public-button-primary inline-flex"
      >
        Support on Ko-fi (from {required.minAmount} {required.currency})
      </a>
      <p className="jo-body-subtle mt-2 text-xs">
        After paying, we email a code to the address you used on Ko-fi. It can
        take a minute.
      </p>
      <form className="mt-4" onSubmit={submit} noValidate>
        <label htmlFor={inputId} className="public-form-label">
          Code from your email
        </label>
        <Input
          id={inputId}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="one-time-code"
          maxLength={11}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {error ? (
          <p id={errorId} role="alert" className="mt-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}
        <Button htmlType="submit" loading={pending} className="mt-3">
          Redeem code
        </Button>
      </form>
    </section>
  );
};

export default KofiSupportPanel;
